import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAlertContext } from "../../../../../../../alert/AlertContext";
import { ApiErrorCode, getApiErrorCode } from "../../../../../../../api/apiErrorHelper";
import { withLocalErrorHandling } from "../../../../../../../api/handleErrorLocally";
import { AssetExpensePartner } from "../../../../../../api/ClientFileApi";
import { useAssetInventoryApi } from "../../../../../../api/ClientFileApiContext";
import { ClientFileQueryKey } from "../../../../../../query/clientFileQueryKeys";

const SECTION = "clients.client.debtSettlement.assetInventory.expensesTab.partner";

export function formatPartnerName(partner: AssetExpensePartner): string {
  return [partner.salutation, partner.firstName, partner.lastName].filter((part) => !!part).join(" ");
}

async function searchErrorKeyOf(error: unknown) {
  switch (await getApiErrorCode(error)) {
    case ApiErrorCode.AssetInventoryExpensePartnerNotFound:
      return `${SECTION}.notFound` as const;
    case ApiErrorCode.AssetInventoryExpensePartnerIsSelf:
      return `${SECTION}.isSelf` as const;
    default:
      return `${SECTION}.searchFailed` as const;
  }
}

export function useExpensePartner(clientId: string) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccess } = useAlertContext();
  const clientsApi = useAssetInventoryApi();
  const [caseNumber, setCaseNumber] = useState<string | null>(null);

  const { data, isFetching } = useQuery({
    queryKey: [ClientFileQueryKey.AssetInventoryExpensePartner, clientId, caseNumber],
    queryFn: async () => {
      if (!clientsApi.getAssetInventoryExpensePartner || !caseNumber) {
        return { partner: null, searchError: null };
      }
      try {
        const partner = await clientsApi.getAssetInventoryExpensePartner(
          { clientId, caseNumber },
          withLocalErrorHandling
        );
        return { partner, searchError: null };
      } catch (error) {
        return { partner: null, searchError: await searchErrorKeyOf(error) };
      }
    },
    enabled: !!caseNumber && !!clientsApi.getAssetInventoryExpensePartner,
    retry: false,
  });

  const shareMutation = useMutation({
    mutationFn: (found: AssetExpensePartner) => {
      if (!clientsApi.shareAssetInventoryExpensesWithPartner) {
        throw new Error("ClientFileApi.assetInventory.shareAssetInventoryExpensesWithPartner is not available");
      }
      return clientsApi.shareAssetInventoryExpensesWithPartner({ clientId, partnerClientId: found.clientId });
    },
    onSuccess: (_data, found) => {
      void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.AssetInventoryExpenses, found.clientId] });
      void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.BankruptcyChecklist, found.clientId] });
      void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.AssetInventoryCompleteness, found.clientId] });
      showSuccess(
        t(`${SECTION}.shareSuccess`, {
          name: formatPartnerName(found),
        })
      );
    },
  });

  return {
    partner: data?.partner ?? null,
    searchError: data?.searchError ? t(data.searchError) : null,
    isSearching: isFetching,
    search: (typed: string) => setCaseNumber(typed.trim() || null),
    share: (found: AssetExpensePartner) => shareMutation.mutate(found),
    isSharing: shareMutation.isPending,
  };
}
